import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../components';
import { Table, Card, Alert, Typography } from 'antd';
import { useSourcesTable } from './hooks';
import { createTableColumns } from './tableConfig';
import SourceDetailsDrawer from './SourceDetailsDrawer';
import EditSourceModal from './components/EditSourceModal';
import SourcesAddTagsModal from './components/SourcesAddTagsModal';
import { DeleteModal, MigrationReadinessDialog, PreflightMonitor } from '../../components';
import './Sources.scss';

const { Title } = Typography;

const SourcesTable = (props) => {
    const {
        combinedSearch,
        selectedRowKeys,
        onSelectedRowKeysChange,
        bulkDeleteTriggerRef,
        bulkConfigureMigrationTriggerRef,
        bulkInspectSourcesTriggerRef,
        addSourceModalOpen,
        onAddSourceModalClose,
        bulkConfigureInSituTriggerRef,
        preflightTriggerRef,
        onDataRefreshNeeded,
    } = props;
    const [tagsModalOpen, setTagsModalOpen] = useState(false);
    const [sourceTags, setSourceTags] = useState([]);
    const [sourceMoveGroup, setSourceMoveGroup] = useState(undefined);
    const [sourceNameForTagsModal, setSourceNameForTagsModal] = useState('');
    const [selectedSourceIdForTags, setSelectedSourceIdForTags] = useState('');
    const {
        // Data
        tableData,
        userOrg,

        // Loading states
        isLoading,
        isFetching,
        isDeleting,
        error,

        // State
        pagination,
        expandedRowKeys,
        effectiveSelectedRowKeys,
        drawerOpen,
        selectedSourceId,
        sourcePreflightData,
        deleteConfirmOpen,
        bulkDeleteMode,
        sourcesToDelete,
        recordToDelete,
        editModalOpen,
        editSourceId,
        highlightedForDeleteId,

        // Migration Readiness Dialog state
        migrationReadinessDialogOpen,
        selectedSourcesForMigration,

        // Source Inspection Dialog state
        sourceInspectionDialogOpen,
        selectedSourcesForInspection,

        // In-Situ Readiness Dialog state
        inSituReadinessDialogOpen,
        selectedSourcesForInSitu,

        // Preflight Modal state
        preflightModalOpen,
        preflightRequests,
        preflightModalConfig,

        // Handlers
        handleRowClick,
        handlePaginationChange,
        handleDeleteSource,
        handleDeleteCancel,
        refetch,

        // Action handlers
        handleStartMigration,
        handleInspectSource,
        handleEditSource,
        handleEditModalClose,
        handleEditModalCancel,
        handleConfigureUpgrade,
        handleDeleteSingleSource,

        // Migration Readiness Dialog handlers
        handleMigrationReadinessDialogClose,
        handleMigrationReadinessConfirm,

        // Source Inspection Dialog handlers
        handleSourceInspectionDialogClose,
        handleSourceInspectionDialogConfirm,

        // Preflight Modal handlers
        handlePreflightModalClose,
        handlePreflightModalComplete,

        // Configuration
        rowSelection,

        // Drawer handlers
        setDrawerOpen,
        setExpandedRowKeys,

        // In-situ confirm handler
        handleInSituConfirm,
        setInSituReadinessDialogOpen,
    } = useSourcesTable({
        combinedSearch,
        selectedRowKeys,
        onSelectedRowKeysChange,
        bulkDeleteTriggerRef,
        bulkConfigureMigrationTriggerRef,
        bulkInspectSourcesTriggerRef,
        addSourceModalOpen,
        onAddSourceModalClose,
        bulkConfigureInSituTriggerRef,
        preflightTriggerRef,
    });

    // Pass refetch function to parent if provided
    useEffect(() => {
        if (onDataRefreshNeeded) {
            onDataRefreshNeeded(refetch);
        }
    }, [onDataRefreshNeeded, refetch]);

    // Create table columns with handlers
    const columns = createTableColumns({
        expandedRowKeys,
        handleRowClick,
        handleStartMigration,
        handleInspectSource,
        handleEditSource,
        handleConfigureUpgrade,
        handleDeleteSingleSource,
        setExpandedRowKeys,
    });

    const handleTagsModalClose = useCallback(() => {
        setSourceMoveGroup(undefined);
        setTagsModalOpen(false);
    }, [])

    const onRowClick = (record) => ({
        onClick: (e) => {
            if (e.target.closest('.tags-column')) {
                e.stopPropagation();
                setTagsModalOpen(true);
                setSourceNameForTagsModal(record.hostname || record.host || 'Source');
                setSourceTags(record.tags || []);
                setSelectedSourceIdForTags(record.id);
                if (record?.move_group) {
                    setSourceMoveGroup(record.move_group);
                }
            } else {
                handleRowClick(e, record);
            }
        },
        className: record.isGroup ? 'group-row' : undefined,
        style: {
            cursor: record.isGroup ? 'default' : 'pointer',
            backgroundColor: record.isGroup
                ? undefined
                : highlightedForDeleteId === record.id
                  ? '#e6f7ff'
                  : effectiveSelectedRowKeys.includes(record.key)
                    ? '#f0f0f0'
                    : undefined,
        },
    });

    // Handle error state
    if (error) {
        return (
            <Alert
                title="Error Loading Sources"
                description={error.message}
                type="error"
                showIcon
                action={
                    <Button size="small" onClick={refetch}>
                        Retry
                    </Button>
                }
            />
        );
    }

    return (
        <div className="sources-table-container">
            <Card>
                <Table
                    className="sources-table"
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                    loading={isLoading || isFetching || isDeleting}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        indentSize: 0,
                        childrenColumnName: 'children',
                        expandIcon: () => null,
                    }}
                    onRow={onRowClick}
                    rowSelection={rowSelection}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: handlePaginationChange,
                        onShowSizeChange: handlePaginationChange,
                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    }}
                />
            </Card>

            <SourceDetailsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sourceId={selectedSourceId}
                userOrg={userOrg}
                preflightData={sourcePreflightData}
            />

            <DeleteModal
                open={deleteConfirmOpen}
                onCancel={handleDeleteCancel}
                onConfirm={() => handleDeleteSource(recordToDelete)}
                title={'Confirm'}
                message={
                    bulkDeleteMode
                        ? `Are you sure you want to delete ${sourcesToDelete.length} selected source${sourcesToDelete.length > 1 ? 's' : ''}?`
                        : 'Are you sure you want to delete the Source?'
                }
                warningMessage="Once deleted, it cannot be recovered."
                confirmLoading={isDeleting}
            />

            <EditSourceModal
                open={editModalOpen || addSourceModalOpen}
                onClose={addSourceModalOpen ? onAddSourceModalClose : handleEditModalClose}
                onCancel={addSourceModalOpen ? onAddSourceModalClose : handleEditModalCancel}
                sourceId={addSourceModalOpen ? null : editSourceId}
                userOrg={userOrg}
            />

            <MigrationReadinessDialog
                open={migrationReadinessDialogOpen}
                onClose={handleMigrationReadinessDialogClose}
                onConfirm={handleMigrationReadinessConfirm}
                selectedSources={selectedSourcesForMigration}
                title="Migration Readiness Check"
            />

            <MigrationReadinessDialog
                open={sourceInspectionDialogOpen}
                onClose={handleSourceInspectionDialogClose}
                onConfirm={handleSourceInspectionDialogConfirm}
                selectedSources={selectedSourcesForInspection}
                title="Source Inspection"
                inspectionMode={true}
            />

            <MigrationReadinessDialog
                open={inSituReadinessDialogOpen}
                onClose={() => setInSituReadinessDialogOpen(false)}
                onConfirm={(data) => handleInSituConfirm({ ...data, selectedSources: selectedSourcesForInSitu })}
                selectedSources={selectedSourcesForInSitu}
                title="Modernization Readiness Check"
                inspectionMode={true}
                okTextOverride="Check Modernization Readiness"
                showIgnoreValidationErrors={true}
            />

            <PreflightMonitor
                open={preflightModalOpen}
                onClose={handlePreflightModalClose}
                onComplete={handlePreflightModalComplete}
                preflightRequests={preflightRequests}
                title={preflightModalConfig.title}
                loadingMessage={preflightModalConfig.loadingMessage}
                proceedOnSuccess={preflightModalConfig.proceedOnSuccess}
                migrationContext={preflightModalConfig.migrationContext}
                inspectSourcesMode={preflightModalConfig.type === 'source_inspection'}
            />

            <SourcesAddTagsModal
                open={tagsModalOpen}
                handleCancel={() => handleTagsModalClose()}
                onClose={() => setTagsModalOpen(false)}
                sourceName={sourceNameForTagsModal}
                initialTags={sourceTags}
                moveGroupId={sourceMoveGroup?.id}
                sourceId={selectedSourceIdForTags}
                onSuccess={() => {
                    handleTagsModalClose();
                    refetch();
                }}
            />
        </div>
    );
};

export default SourcesTable;
